/**
 * Podcast feed loader — fetches and parses the BAM podcast RSS feed.
 *
 * Feed URL is configurable via REACT_APP_PODCAST_RSS_URL env var.
 * Defaults to the By Any Means Coaches Podcast Buzzsprout feed.
 *
 * Browser CORS considerations:
 *  - Buzzsprout RSS does NOT set CORS headers, so a direct fetch from the browser fails.
 *  - We use a lightweight public CORS proxy (api.allorigins.win) as a fallback.
 *  - For production you should set REACT_APP_PODCAST_PROXY_URL to your own proxy
 *    (a Supabase Edge Function or a Vercel serverless route) for reliability.
 *
 * Caching:
 *  - Parsed episodes are cached in localStorage for 1 hour to keep things fast
 *    and reduce load on the proxy.
 */

const DEFAULT_FEED = "https://feeds.buzzsprout.com/1911095.rss";
const FEED_URL = process.env.REACT_APP_PODCAST_RSS_URL || DEFAULT_FEED;

// Primary strategy: our own Vercel Serverless Function at /api/podcast.
// It fetches the RSS server-side (no CORS), caches at the edge for 10 min,
// and has zero third-party dependencies.
//
// Fallback: try a chain of public CORS proxies in case the serverless
// function is misconfigured or down for some reason.
const LOCAL_PROXY = "/api/podcast?feed="; // same-origin, no CORS
const PUBLIC_PROXY_FALLBACKS = [
  process.env.REACT_APP_PODCAST_PROXY_URL,
  "https://corsproxy.io/?",
  "https://api.codetabs.com/v1/proxy?quest=",
  "https://api.allorigins.win/raw?url=",
].filter(Boolean);

const CACHE_KEY = "bam_podcast_cache_v1";
const CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function loadCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function saveCache(data) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {}
}

function getTagText(el, tag) {
  const node = el.getElementsByTagName(tag)[0];
  if (!node) return "";
  return node.textContent?.trim() || "";
}

function getTagAttr(el, tag, attr) {
  const node = el.getElementsByTagName(tag)[0];
  if (!node) return "";
  return node.getAttribute(attr) || "";
}

function stripHtml(html) {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return (div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
}

function parseDurationToSeconds(str) {
  if (!str) return 0;
  // Formats: "HH:MM:SS", "MM:SS", or raw seconds like "4091"
  if (/^\d+$/.test(str)) return parseInt(str, 10);
  const parts = str.split(":").map((n) => parseInt(n, 10) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

export function formatDuration(seconds) {
  if (!seconds || seconds < 0) return "";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min`;
}

export function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return dateStr;
  }
}

/**
 * Fetch and parse the podcast RSS feed.
 * @param {object} options
 * @param {boolean} [options.bypassCache] - skip localStorage cache
 * @returns {Promise<{podcast: object, episodes: Array}>}
 */
export async function fetchPodcastFeed({ bypassCache = false } = {}) {
  if (!bypassCache) {
    const cached = loadCache();
    if (cached) return cached;
  }

  // Helper: try a single URL with a short timeout, return text or throw.
  const tryFetch = async (url) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const text = await res.text();
      if (!text || text.length < 100 || (!text.includes("<rss") && !text.includes("<channel"))) {
        throw new Error("Non-RSS response");
      }
      return text;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // 1. Try our own /api/podcast serverless function first (same-origin, no CORS).
  //    In local dev this won't exist unless you're running `vercel dev`, so it'll
  //    gracefully fall through to the public proxies.
  let xml = null;
  let lastErr = null;
  try {
    xml = await tryFetch(LOCAL_PROXY + encodeURIComponent(FEED_URL));
  } catch (e) {
    lastErr = e;
    console.info("[podcast] /api/podcast unavailable, trying public proxies", e.message);
  }

  // 2. Fall back to public CORS proxies if the serverless function failed.
  if (!xml) {
    for (const proxy of PUBLIC_PROXY_FALLBACKS) {
      try {
        xml = await tryFetch(proxy + encodeURIComponent(FEED_URL));
        break;
      } catch (e) {
        lastErr = e;
      }
    }
  }

  if (!xml) {
    throw new Error("All podcast sources failed: " + (lastErr?.message || "unknown"));
  }

  // Parse XML
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, "text/xml");
  const parseErr = doc.getElementsByTagName("parsererror")[0];
  if (parseErr) throw new Error("Podcast RSS parse error");

  const channel = doc.getElementsByTagName("channel")[0];
  if (!channel) throw new Error("Podcast RSS has no channel element");

  // Podcast-level metadata
  const podcast = {
    title: getTagText(channel, "title"),
    description: stripHtml(getTagText(channel, "description")),
    link: getTagText(channel, "link"),
    image:
      getTagAttr(channel, "itunes:image", "href") ||
      channel.getElementsByTagName("image")[0]?.getElementsByTagName("url")[0]?.textContent?.trim() ||
      "",
    author: getTagText(channel, "itunes:author") || getTagText(channel, "author"),
  };

  // Episodes — parse each item defensively so one bad item doesn't break the list
  const items = Array.from(channel.getElementsByTagName("item"));
  const episodes = items
    .map((item) => {
      try {
        const enclosure = item.getElementsByTagName("enclosure")[0];
        const audioUrl = enclosure?.getAttribute("url") || "";
        const durationStr =
          getTagText(item, "itunes:duration") ||
          getTagText(item, "duration");
        return {
          guid: getTagText(item, "guid") || audioUrl || String(Math.random()),
          title: getTagText(item, "title") || "Untitled Episode",
          description: stripHtml(getTagText(item, "description") || getTagText(item, "itunes:summary")),
          pubDate: getTagText(item, "pubDate"),
          audioUrl,
          duration: parseDurationToSeconds(durationStr),
          episodeNumber: parseInt(getTagText(item, "itunes:episode"), 10) || null,
          season: parseInt(getTagText(item, "itunes:season"), 10) || null,
          image:
            getTagAttr(item, "itunes:image", "href") || podcast.image,
        };
      } catch (e) {
        console.warn("[podcast] failed to parse item", e);
        return null;
      }
    })
    .filter(Boolean);

  const result = { podcast, episodes };
  saveCache(result);
  return result;
}
